import { Link, Outlet, useLocation } from 'react-router-dom';
import { AccountSwitcher } from '../auth';
import { useI18n } from '../i18n';
import { ArrowUpTrayIcon } from '../storage/icons';
import { AppShell } from './AppShell';
import { BreadcrumbProvider, HeaderBreadcrumb } from './Breadcrumb';
import { DropTargetProvider, useIsDropActive } from './drop-target';
import { RouteErrorBoundary } from './ErrorBoundary';

/**
 * Coquille AUTHENTIFIÉE partagée — en-tête PERSISTANT sur tous les écrans.
 *
 * Route de LAYOUT : rend `AppShell` (bandeau MAJ, lien d'évitement, en-tête) une
 * seule fois et délègue le contenu de `main` aux routes filles via `<Outlet>`.
 *
 * En-tête : à gauche le FIL D'ARIANE complet (Maison → espace → chemin de dossiers, le dernier
 * maillon tenant lieu de titre de page), à droite le bouton « Déposer » et le menu de compte
 * (multi-login + thème + langue + « Signaler un bug » + déconnexion).
 *
 * `BreadcrumbProvider` enveloppe l'en-tête ET l'`<Outlet>` : les pages filles poussent leur chemin
 * (`useSetBreadcrumb`) et l'en-tête le lit — les deux doivent donc partager le même contexte.
 * `DropTargetProvider` (XFILE-84) est monté selon le MÊME principe et pour la même raison : la page
 * inscrit sa cible de dépôt (`useDropFiles`), l'en-tête lit l'état du glisser (`useIsDropActive`)
 * pour allumer le bouton « Déposer », et le voile plein écran est rendu par le provider.
 *
 * Le bouton « Déposer » est OUVERT À TOUT utilisateur authentifié (Lot 6 ; XFILE-28) — aucune garde
 * de rôle : `/upload` vise par défaut le rangement `auto`, où le dépôt est autorisé à tous côté
 * serveur (droit de dépôt décidé PAR ESPACE, plus par le rôle global).
 */
export function AppChrome(): React.JSX.Element {
  return (
    <BreadcrumbProvider>
      <DropTargetProvider>
        <AppShell nav={<HeaderBreadcrumb />} headerActions={<HeaderActions />}>
          {/* Frontière de ROUTE : un plantage de rendu d'une page affiche un repli
              diagnosticable DANS `main` — l'en-tête persistant reste utilisable pour
              naviguer ailleurs — au lieu d'une page blanche. */}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </AppShell>
      </DropTargetProvider>
    </BreadcrumbProvider>
  );
}

/**
 * Actions de fin d'en-tête. Composant SÉPARÉ, et pas un fragment inline dans `AppChrome` : le
 * bouton « Déposer » lit `useIsDropActive()`, donc il doit être rendu SOUS le provider — que
 * `AppChrome` monte lui-même (un hook ne voit jamais le contexte que son propre composant fournit).
 */
function HeaderActions(): React.JSX.Element {
  const { t } = useI18n();
  // Sur l'écran de dépôt (`/upload`), le bouton « Déposer » de l'en-tête ferait DOUBLON
  // avec le bouton d'envoi du formulaire → on le masque là (évite deux « Déposer »).
  const onUploadScreen = useLocation().pathname === '/upload';
  const dropActive = useIsDropActive();

  return (
    <div className="flex items-center gap-1">
      {!onUploadScreen && (
        <Link
          to="/upload"
          aria-label={t('nav.deposit')}
          /*
           * ANCRE VISUELLE du glisser (XFILE-84) : pendant un glisser de fichiers, le bouton
           * s'allume (fond plus contrasté + anneau) pour désigner ce que le geste vise. Purement
           * DÉCORATIF — le message, lui, est porté par le texte du voile, jamais par cet état ;
           * aucune information ne repose donc sur la seule couleur.
           */
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
            dropActive
              ? 'bg-brand-strong-hover outline-2 outline-offset-2 outline-brand'
              : 'bg-brand-strong hover:bg-brand-strong-hover'
          }`}
        >
          <ArrowUpTrayIcon className="size-5" />
          <span className="hidden sm:inline">{t('nav.deposit')}</span>
        </Link>
      )}
      {/* L'entrée « Superadmin » vit désormais DANS le menu Paramètres (roue crantée),
          masquée pour tout rôle non superadmin — plus de bouton bouclier séparé. */}
      <AccountSwitcher />
    </div>
  );
}
